import { Link } from "react-router-dom";
import { ShieldCheck, MapPin, BedDouble, Bath, Maximize, Scale, Check, Star } from "lucide-react";
import { fileUrl, formatINR } from "../lib/api";
import { useCompare } from "../context/CompareContext";

export default function PropertyCard({ property, compact = false }) {
    const { isIn, add, remove } = useCompare();
    const inCompare = isIn(property.id);

    const img = property.images?.[0]
        ? fileUrl(property.images[0])
        : "https://images.unsplash.com/photo-1638454795595-0a0abf68614d?crop=entropy&cs=srgb&fm=jpg&q=85";
    const isRent = property.listing_type === "rent";

    const toggleCompare = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (inCompare) remove(property.id);
        else add(property.id);
    };

    return (
        <Link
            to={`/properties/${property.id}`}
            data-testid={`property-card-${property.id}`}
            className="group block rounded-2xl overflow-hidden bg-white border border-navy-500/10 hover:border-gold-500/50 hover-lift transition-colors"
        >
            <div className="relative img-zoom aspect-[16/11]">
                <img src={img} alt={property.title} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#002657]/85 via-[#002657]/30 to-transparent pointer-events-none" />
                {property.featured && (
                    <div data-testid="property-card-featured-badge" className="absolute top-3 left-3 px-3 py-1 rounded-full bg-gradient-to-r from-gold-500 to-amber-500 text-navy-500 text-[10px] font-extrabold uppercase tracking-widest flex items-center gap-1 shadow-lg shadow-gold-500/40 ring-1 ring-navy-500/10">
                        <Star className="w-3 h-3 fill-current" /> Featured
                    </div>
                )}
                {property.verified && !property.featured && (
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 shadow-md">
                        <ShieldCheck className="w-3 h-3" /> Verified
                    </div>
                )}
                <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-[#002657]/85 backdrop-blur-md text-gold-300 text-[10px] font-semibold uppercase tracking-widest border border-gold-500/40">
                    For {property.listing_type}
                </div>
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-on-image">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-gold-300 mb-0.5 font-semibold">
                            {property.property_type}{property.subtype ? ` · ${property.subtype}` : ""}
                        </div>
                        <div className="font-heading font-bold text-white text-lg leading-tight line-clamp-1 drop-shadow-md">
                            {property.title}
                        </div>
                    </div>
                </div>
            </div>
            <div className="p-4 sm:p-5 space-y-2.5 sm:space-y-3">
                <div className="flex items-baseline justify-between gap-2">
                    <div>
                        <div className="text-xl sm:text-2xl font-heading font-bold text-gold-600">
                            {formatINR(property.price)}
                            {isRent && <span className="text-xs font-medium text-navy-500/60 ml-1">/mo</span>}
                        </div>
                    </div>
                    <div className="text-[11px] sm:text-xs text-navy-500/65 flex items-center gap-1 text-right">
                        <MapPin className="w-3.5 h-3.5 text-gold-600 shrink-0" />
                        <span className="line-clamp-1">{property.locality ? `${property.locality}, ` : ""}{property.city}</span>
                    </div>
                </div>
                {!compact && (
                    <div className="flex items-center justify-between pt-2 border-t border-navy-500/10 text-xs sm:text-sm text-navy-500/80">
                        <div className="flex items-center gap-3 sm:gap-4">
                            {property.bhk != null && (
                                <span className="flex items-center gap-1.5">
                                    <BedDouble className="w-4 h-4 text-gold-600" /> {property.bhk} BHK
                                </span>
                            )}
                            {property.bathrooms != null && (
                                <span className="flex items-center gap-1.5">
                                    <Bath className="w-4 h-4 text-gold-600" /> {property.bathrooms}
                                </span>
                            )}
                            {property.area_sqft != null && (
                                <span className="flex items-center gap-1.5">
                                    <Maximize className="w-4 h-4 text-gold-600" /> {property.area_sqft} sqft
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={toggleCompare}
                            data-testid={`property-compare-toggle-${property.id}`}
                            aria-pressed={inCompare}
                            title={inCompare ? "Remove from compare" : "Add to compare"}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-bold uppercase tracking-wider transition-colors ${
                                inCompare
                                    ? "bg-gold-500 text-navy-900 border-gold-500"
                                    : "bg-white text-navy-500/80 border-navy-500/15 hover:border-gold-500/60 hover:text-gold-700"
                            }`}
                        >
                            {inCompare ? <Check className="w-3 h-3" /> : <Scale className="w-3 h-3" />}
                            {inCompare ? "Added" : "Compare"}
                        </button>
                    </div>
                )}
            </div>
        </Link>
    );
}
